import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { v4 as uuidv4 } from 'uuid';
import { isEqual } from 'date-fns';
import { Formik, Form as FormikForm, validateYupSchema, yupToFormErrors } from 'formik';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';

import withDatePicker from 'core/components/with-date-picker';
import useSession from 'core/hooks/use-session';
import { FormField } from 'core/components/form';
import { driversLicenseNumberSchema } from 'core/helpers/drivers-license-number-validations';
import useStyles from './add-form.styles';

import { validUSState } from 'common/helpers/yup-helpers';

Yup.addMethod(Yup.mixed, 'validUSState', validUSState);

const basicSchema = Yup.object().shape({
  firstName: Yup.string().required('First Name is required!'),
  lastName: Yup.string().required('Last Name is required'),
  driversLicenseNumber: driversLicenseNumberSchema.nullable(),
  driversLicenseState: Yup.string().validUSState('driversLicenseState').nullable(),
  dateOfBirth: Yup.string().required('Date of birth is required!')
});

const completeSchema = Yup.object().shape({
  firstName: Yup.string().required('First Name is required!'),
  lastName: Yup.string().required('Last Name is required'),
  driversLicenseNumber: driversLicenseNumberSchema.required('License Number is required'),
  driversLicenseState: Yup.string().validUSState('driversLicenseState').required('License State is required'),
  dateOfBirth: Yup.string().required('Date of birth is required!')
});

function AddForm({ onAdd, label, disabled, addContent, currentPeopleOrDrivers }) {
  const classes = useStyles();
  const { canBypassDriversLicenseCheck } = useSession();

  const validate = useCallback(
    (values) => {
      try {
        const errors = {};
        if (values.firstName && values.lastName && values.dateOfBirth) {
          const existingPerson = currentPeopleOrDrivers?.find((d) => {
            const existingPersonOrDriverDOB = new Date(d.dateOfBirth);
            const addedPersonDOB = new Date(values.dateOfBirth);
            return (
              values.firstName.toUpperCase() === d.firstName.toUpperCase() &&
              values.lastName.toUpperCase() === d.lastName.toUpperCase() &&
              isEqual(existingPersonOrDriverDOB, addedPersonDOB)
            );
          });

          if (existingPerson) {
            errors.existingDriver = existingPerson.excludeDriver
              ? "We're sorry, this driver is not eligible to be covered by Branch."
              : "Oops! It looks like you've already added this person.";
            errors.firstName = true;
            errors.lastName = true;
            errors.dateOfBirth = true;
            return errors;
          }
        }
        if (canBypassDriversLicenseCheck) {
          validateYupSchema(values, basicSchema, true);
        } else {
          validateYupSchema(values, completeSchema, true);
        }
        return errors;
      } catch (err) {
        return yupToFormErrors(err);
      }
    },
    [currentPeopleOrDrivers, canBypassDriversLicenseCheck]
  );

  return (
    <Formik
      initialValues={{
        id: uuidv4(),
        firstName: '',
        lastName: '',
        driversLicenseNumber: '',
        driversLicenseState: '',
        dateOfBirth: new Date(),
        isCoApplicant: false,
        isPrimary: false
      }}
      validate={validate}
      onSubmit={onAdd}
    >
      {({ errors }) => (
        <FormikForm className={classes.container}>
          {addContent}
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={4.5}>
              <FormField
                id="firstName"
                name="firstName"
                type="string"
                label="First Name"
                mode="light"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={4.5}>
              <FormField
                id="lastName"
                name="lastName"
                type="string"
                label="Last Name"
                mode="light"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={3}>
              <FormField
                id="dateOfBirth"
                name="dateOfBirth"
                type="date"
                label="Date of Birth"
                mode="light"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField
                id="driversLicenseState"
                name="driversLicenseState"
                type="state"
                label="License State"
                mode="light"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField
                id="driversLicenseNumber"
                name="driversLicenseNumber"
                type="string"
                label="License Number"
                mode="light"
                disabled={disabled}
              />
            </Grid>
            <Grid container item xs={4} alignItems="end">
              <Button
                key="addButton"
                className={classes.button}
                type="submit"
                variant="contained"
                color="secondary"
                disabled={disabled}
              >
                {label || 'Add People'}
              </Button>
            </Grid>
          </Grid>
          {errors.existingDriver && <span className={classes.error}>{errors.existingDriver}</span>}
        </FormikForm>
      )}
    </Formik>
  );
}

AddForm.propTypes = {
  onAdd: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  addContent: PropTypes.node,
  currentPeopleOrDrivers: PropTypes.array.isRequired
};

AddForm.defaultProps = {
  disabled: false,
  addContent: null
};

export default withDatePicker(AddForm);
